import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "./Icon";
import { useBookmarkContext } from "../hooks/useBookmarkContext";
import {
  exportCardAsImage,
  exportCardAsBlob,
  generateCardFilename,
} from "../utils/cardExport";
import { getPaletteForSection } from "../utils/colorPalettes";
import { logError } from "../utils/logger";
import type { NewNarrativeData, BookmarkData } from "../types/narrative";

interface BookmarksOverlayProps {
  narrativeData: NewNarrativeData;
  isOpen: boolean;
  onClose: () => void;
}

interface BookmarkReference {
  groupId: string;
  bookmarkData: BookmarkData;
}

// CSS overrides to force cloned content to expand to full width
// This removes the narrow phone-frame constraints from the original DOM
const snapshotStyleOverrides = `
  .bookmark-snapshot-content > div {
    max-width: none !important;
    width: 100% !important;
  }
  .bookmark-snapshot-content * {
    max-width: none !important;
  }
`;

// Base dimensions for the snapshot content layout
// Content reflows to this width, then gets scaled to fit target size
const SNAPSHOT_BASE_WIDTH = 500;
const SNAPSHOT_BASE_HEIGHT = 500;

// Component to render a bookmark snapshot with gradient background
function BookmarkSnapshot({
  bookmarkData,
  className = "",
  size = 400,
}: {
  bookmarkData: BookmarkData;
  className?: string;
  size?: number; // Square size in pixels (1:1 aspect ratio)
}) {
  // Get section palette for background gradient (supports inline palette stored in bookmark)
  const palette = getPaletteForSection(
    bookmarkData.sectionId,
    bookmarkData.colorPalette,
  );

  // Scale factor to fit base dimensions into target size
  const scale = size / SNAPSHOT_BASE_WIDTH;

  return (
    <div
      className={`relative overflow-hidden rounded-xl ${className}`}
      style={{
        // Square container
        width: `${size}px`,
        height: `${size}px`,
      }}
    >
      {/* Inject style overrides */}
      <style>{snapshotStyleOverrides}</style>

      {/* Gradient background */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(to bottom, ${palette.from} 0%, ${palette.via} 50%, ${palette.from} 100%)`,
        }}
      />

      {/* Scaled content container */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          pointerEvents: "none",
          userSelect: "none",
        }}
      >
        <div
          style={{
            width: `${SNAPSHOT_BASE_WIDTH}px`,
            height: `${SNAPSHOT_BASE_HEIGHT}px`,
            transform: `scale(${scale})`,
            transformOrigin: "center center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px",
          }}
        >
          {/* Content wrapper with class for CSS targeting */}
          <div
            className="bookmark-snapshot-content w-full"
            dangerouslySetInnerHTML={{ __html: bookmarkData.snapshot }}
          />
        </div>
      </div>
    </div>
  );
}

export function BookmarksOverlay({
  narrativeData,
  isOpen,
  onClose,
}: BookmarksOverlayProps) {
  const { bookmarks, setBookmark } = useBookmarkContext();
  const [selectedBookmark, setSelectedBookmark] =
    useState<BookmarkReference | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const exportCardRef = useRef<HTMLDivElement>(null);

  // Handle ESC key to close overlay
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (selectedBookmark) {
          // If detail view is open, close it first
          setSelectedBookmark(null);
        } else {
          // Otherwise close the overlay
          onClose();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, selectedBookmark, onClose]);

  // Collect all bookmarked items
  const bookmarkedItems: BookmarkReference[] = Object.entries(bookmarks)
    .filter(([, data]) => data !== null)
    .map(([groupId, data]) => ({
      groupId,
      bookmarkData: data as BookmarkData,
    }));

  const handleCardClick = (bookmark: BookmarkReference) => {
    setSelectedBookmark(bookmark);
  };

  const handleCloseDetail = () => {
    setSelectedBookmark(null);
  };

  const handleRemoveBookmark = () => {
    if (selectedBookmark) {
      setBookmark(selectedBookmark.groupId, null);
      setSelectedBookmark(null);
    }
  };

  const handleDownload = async () => {
    if (!exportCardRef.current || !selectedBookmark) return;

    setIsExporting(true);
    try {
      const filename = generateCardFilename(
        narrativeData.metadata.customer_name,
        selectedBookmark.bookmarkData.sectionId,
        narrativeData.metadata.timestamp_generated,
      );

      // Detect iOS - use Web Share API for better Photos integration
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      if (isIOS && navigator.share && navigator.canShare) {
        const blob = await exportCardAsBlob(exportCardRef.current);
        const file = new File([blob], filename, { type: "image/png" });

        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "Save Insight Card",
          });
          setIsExporting(false);
          return;
        }
      }

      await exportCardAsImage(exportCardRef.current, filename);
    } catch (error: unknown) {
      if (error instanceof Error && error.name === "AbortError") {
        setIsExporting(false);
        return;
      }
      logError("Export failed:", error);
      alert("Failed to download card. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleShare = async () => {
    if (!selectedBookmark || !exportCardRef.current) return;

    if (navigator.share && navigator.canShare) {
      setIsExporting(true);
      try {
        const blob = await exportCardAsBlob(exportCardRef.current);
        const filename = generateCardFilename(
          narrativeData.metadata.customer_name,
          selectedBookmark.bookmarkData.sectionId,
          narrativeData.metadata.timestamp_generated,
        );
        const file = new File([blob], filename, { type: "image/png" });

        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "Insight Snapshot",
          });
          setIsExporting(false);
          return;
        }
      } catch (error: unknown) {
        setIsExporting(false);
        if (error instanceof Error && error.name === "AbortError") return;
      }
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex flex-col"
        style={{
          background: "rgba(0, 0, 0, 0.85)",
          backdropFilter: "blur(12px)",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Icon icon="bookmark" className="text-emerald-400 text-xl" />
            <h2 className="text-white text-xl font-semibold">Your Bookmarks</h2>
            <span className="text-white/50 text-sm">
              ({bookmarkedItems.length}{" "}
              {bookmarkedItems.length === 1 ? "insight" : "insights"})
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {bookmarkedItems.length === 0 ? (
            /* Empty state */
            <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="text-6xl">📚</div>
                <h3 className="text-2xl font-semibold text-white">
                  No bookmarks yet
                </h3>
                <p className="text-white/70 max-w-sm">
                  Bookmark insights throughout the presentation to save them for
                  later.
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onClose}
                  className="mt-6 px-6 py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-white font-medium transition-colors"
                >
                  Continue Browsing
                </motion.button>
              </motion.div>
            </div>
          ) : (
            /* Card grid */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {bookmarkedItems.map((bookmark, index) => (
                <motion.div
                  key={bookmark.groupId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="cursor-pointer hover:scale-105 transition-transform duration-200"
                  onClick={() => handleCardClick(bookmark)}
                >
                  <BookmarkSnapshot
                    bookmarkData={bookmark.bookmarkData}
                    size={280}
                  />
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Fixed close button - matches PinnedBookmarkButton position */}
        <button
          onClick={onClose}
          className="fixed bottom-4 right-4 z-[10001] w-14 h-14 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
          aria-label="Close bookmarks"
        >
          <Icon icon="xmark" className="text-2xl" />
        </button>

        {/* Detail Modal */}
        <AnimatePresence>
          {selectedBookmark && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[10000] flex items-center justify-center"
              style={{
                background: "rgba(0, 0, 0, 0.7)",
                backdropFilter: "blur(8px)",
              }}
              onClick={handleCloseDetail}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="relative max-w-2xl w-full space-y-6 p-4"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close button */}
                <button
                  onClick={handleCloseDetail}
                  className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white transition-colors"
                  aria-label="Close"
                >
                  <Icon icon="xmark" className="text-xl" />
                </button>

                {/* Card display - larger preview */}
                <div className="flex justify-center">
                  <BookmarkSnapshot
                    bookmarkData={selectedBookmark.bookmarkData}
                    size={400}
                    className="shadow-2xl"
                  />
                </div>

                {/* Action buttons */}
                <div className="flex flex-col gap-4">
                  {/* Download and Share buttons */}
                  <div className="flex gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleDownload}
                      disabled={isExporting}
                      className="flex-1 px-6 py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Icon icon="arrow-down-to-bracket" className="mr-2" />
                      {isExporting ? "Exporting..." : "Download"}
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleShare}
                      disabled={isExporting}
                      className="flex-1 px-6 py-3 bg-white hover:bg-white/90 rounded-full text-gray-900 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Icon icon="arrow-up-from-bracket" className="mr-2" />
                      Share
                    </motion.button>
                  </div>

                  {/* Remove bookmark */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleRemoveBookmark}
                    className="px-6 py-2 text-white/50 hover:text-white/70 text-sm transition-colors"
                  >
                    Remove Bookmark
                  </motion.button>
                </div>
              </motion.div>

              {/* Hidden export card (full resolution 1080x1080) */}
              <div className="fixed -left-[9999px] -top-[9999px]">
                <div ref={exportCardRef}>
                  <BookmarkSnapshot
                    bookmarkData={selectedBookmark.bookmarkData}
                    size={1080}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}
